import { InputGroup } from "~/components/ui/input-group"
import { InputGroupAddon } from "~/components/ui/input-group"
import { InputGroupText } from "~/components/ui/input-group"
import { InputGroupInput } from "~/components/ui/input-group"
import { Field, FieldLabel } from "~/components/ui/field"

import { useQueryClient } from "@tanstack/react-query"
import { PlusIcon } from "lucide-react"
import { type FormEvent, type ReactNode, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "~/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import { useOrganization } from "~/hooks/useOrganization"
import { api } from "~/trpc/react"

export function TeamCreateDialog({ children }: { children: ReactNode }) {
	const navigate = useNavigate()
	const { organization } = useOrganization()
	const utils = api.useUtils()
	const queryClient = useQueryClient()
	const [open, setOpen] = useState(false)
	const [name, setName] = useState("")
	const [slug, setSlug] = useState("")
	const [slugEdited, setSlugEdited] = useState(false)
	const createTeam = api.team.createTeam.useMutation()

	const reset = () => {
		setName("")
		setSlug("")
		setSlugEdited(false)
	}

	const submit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		if (!organization) return

		const result = await createTeam
			.mutateAsync({
				name: name.trim(),
				slug,
				organizationId: organization.id
			})
			.catch((error) => {
				toast.error(error.message || "Failed to create team")
				return null
			})
		if (!result) return
		if (result.message || !result.teamId) {
			toast.error(result.message || "Failed to create team")
			return
		}

		queryClient.clear()
		await Promise.all([
			utils.user.getTeams.prefetch({ organizationId: organization.id }),
			utils.user.getOrgs.prefetch(),
			utils.team.getBySlug.prefetch({
				organizationSlug: organization.slug,
				teamSlug: slug
			})
		])
		setOpen(false)
		reset()
		navigate(`/${organization.slug}/${slug}`)
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => {
				setOpen(nextOpen)
				if (!nextOpen && !createTeam.isPending) reset()
			}}
		>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create a team</DialogTitle>
					<DialogDescription>
						If the initial team wasn't enough, why not create another?
					</DialogDescription>
				</DialogHeader>
				<form className="flex flex-col gap-4" onSubmit={submit}>
					<Field>
						<FieldLabel htmlFor="create-team-name">Team name</FieldLabel>
						<Input
							id="create-team-name"
							value={name}
							maxLength={24}
							autoFocus
							placeholder="Design"
							onChange={(event) => {
								const nextName = event.target.value
								setName(nextName)
								if (!slugEdited) {
									setSlug(
										nextName
											.toLowerCase()
											.trim()
											.replace(/[^a-z0-9]+/g, "-")
											.replace(/^-+|-+$/g, "")
											.slice(0, 12)
									)
								}
							}}
						/>
					</Field>
					<div className="flex flex-col gap-2">
						<Label htmlFor="create-team-slug">Team URL</Label>
						<InputGroup>
							<InputGroupAddon>
								<InputGroupText>/{organization?.slug}/</InputGroupText>
							</InputGroupAddon>
							<InputGroupInput
								id="create-team-slug"
								value={slug}
								maxLength={12}
								pattern="[a-z0-9-]{3,12}"

								placeholder="design"
								onChange={(event) => {
									setSlugEdited(true)
									setSlug(
										event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
									)
								}}
							/>
						</InputGroup>
					</div>
					<Button
						type="submit"
						disabled={
							createTeam.isPending ||
							!name.trim() ||
							!/^[a-z0-9-]{3,12}$/.test(slug)
						}
						pending={createTeam.isPending}
						icon={<PlusIcon className="size-4" />}
					>
						Create team
					</Button>
				</form>
			</DialogContent>
		</Dialog>
	)
}
